import { ImageWithLoader } from '@overlayqa/shared/ui';
import { useState } from 'react';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import { Refresh01Icon } from '@hugeicons/core-free-icons';
import { getProjectIconAndColor } from '@overlayqa/shared';
import { resolveAssetUrl } from '@/lib/api/client';
import { useRefreshProjectThumbnail } from '../../../hooks/useProjects';
import { log } from '@/lib/logger';

interface ThumbnailFieldProps {
  project: {
    id: string;
    name: string;
    primaryDomain?: string | null;
    thumbnailUrl?: string | null;
    icon?: string | null;
    iconColor?: string | null;
  };
}

/**
 * The project thumbnail at the top of the Project Settings General tab, with
 * Retake (Emily, 2026-09-17: "allow user to retake thumbnail image", "retake
 * should be on the botom right").
 *
 * Retake re-captures the project's SAVED URL through POST
 * /projects/:id/refresh-thumbnail. The server answers a capture that produced
 * no image with 502 and a message naming the site, shown verbatim; the old
 * image stays until a new one arrives. With no URL there is nothing to capture,
 * so Retake is inert and the hint says why.
 */
export function ThumbnailField({ project }: ThumbnailFieldProps) {
  const refresh = useRefreshProjectThumbnail();
  // Local, like the settings page's other busy flags: the label must reflect
  // THIS press, from the click until the request settles.
  const [retaking, setRetaking] = useState(false);
  const domain = project.primaryDomain?.trim() || null;
  const { icon, color } = getProjectIconAndColor(project);

  const handleRetake = async () => {
    setRetaking(true);
    try {
      await refresh.mutateAsync(project.id);
    } catch (err) {
      log.error('[ThumbnailField] Retake failed', { projectId: project.id, error: err instanceof Error ? err.message : String(err) });
      toast.error(err instanceof Error && err.message ? err.message : 'Could not retake the thumbnail. Please try again.');
    } finally {
      setRetaking(false);
    }
  };

  return (
    <fieldset className="mb-5">
      <legend className="field-label">Thumbnail</legend>
      <div className="relative h-[280px] rounded-lg bg-gray-100 border border-gray-200 overflow-hidden">
        {project.thumbnailUrl ? (
          <ImageWithLoader fill
            src={resolveAssetUrl(project.thumbnailUrl)}
            alt={`Thumbnail of ${domain ?? 'this project'}`}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" aria-hidden="true">
            <svg className="w-10 h-10" style={{ color: color.hex }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d={icon.path} />
            </svg>
          </div>
        )}
        <button
          type="button"
          className="btn-secondary btn-sm absolute bottom-2 right-2 shadow-sm"
          onClick={() => void handleRetake()}
          disabled={!domain || retaking}
        >
          <HugeiconsIcon icon={Refresh01Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" aria-hidden="true" />
          <span className="text-cap-trim">{retaking ? 'Retaking...' : 'Retake'}</span>
        </button>
      </div>
      <p className="field-hint mt-1">
        {domain ? `Captured from ${domain}. Retake it after the site changes.` : 'Add a URL to capture a thumbnail.'}
      </p>
    </fieldset>
  );
}
